import './components/Tooltip.css';
import './style.css';

import React from 'react';
import { Redirect, Route as UnauthorizedRoute, Switch } from 'react-router-dom';

import Economic from './components/Economic/Economic';
import Header from './components/Header';
import * as pages from './pages';
import NotFound from './pages/errors/NotFound/NotFound';
import UnexpectedError from './pages/errors/UnexepectedError/UnexpectedError';
import { EContext } from './shared/components/EContext';
import ErrorBoundary from './shared/components/ErrorBoundary';
import Route from './shared/components/Route';
import { asyncLoader } from './shared/util/components';
import isWindowUndocked from './shared/util/utilities/isWindowUndocked';
import { useIntroductionTour } from './components/Header/IntroductionTour/useIntroductionTour';
import VismaConnectCongratsDialog from './shared/components/modals/VismaConnectCongratsDialog';
import VismaConnectJoinDialog from './shared/components/modals/VismaConnectJoinDialog';

const TrialExpiredDialog = asyncLoader(() =>
    import(/* webpackChunkName: "trial-expired-dialog" */ './shared/components/modals/TrialExpiredDialog')
);
const GettingStartedDialog = asyncLoader(() =>
    import(/* webpackChunkName: "getting-started-dialog" */ './forms/GettingStartedSplash/GettingStartedDialog')
);
const LegacyDialogRoutes = asyncLoader(() => import(/* webpackChunkName: "legacy-dialogs" */ './shared/LegacyDialogRoutes'));

function BaseRoute() {
    const { routes } = React.useContext(EContext);
    const baseRoute = routes['/'];

    if (baseRoute && baseRoute[0] === '/') {
        return <Redirect to={baseRoute} />;
    } else if (baseRoute === 'AppStore/developer') {
        // temporary fix
        return <Redirect to="/developer" />;
    }

    return <Redirect to="/home" />;
}

export default function Application(props) {
    return (
        <Economic {...props}>
            {!isWindowUndocked() && <Header />}

            <ErrorBoundary component={UnexpectedError}>
                <Switch>
                    <Route exact path="/" component={BaseRoute} />

                    <Route path="/home" component={pages.Home} />
                    <Route path="/developer" component={pages.Developer} />
                    <Route path="/partner-api" component={pages.PartnerApi} />
                    <Route path="/system" component={pages.System} />
                    <Route path="/basic" component={pages.Basic} />
                    <Route path="/admin" component={pages.Admin} />
                    <Route path="/custom" component={pages.Custom} />

                    <Route path="/sales" component={pages.Sales} />
                    <Route path="/accounting" component={pages.Accounting} />
                    <Route path="/reports" component={pages.Reports} />
                    <Route path="/projects" component={pages.Projects} />
                    <Route path="/inventory" component={pages.Inventory} />
                    <Route path="/journal-pro" component={pages.JournalPro} />
                    <Route path="/market" component={pages.Market} />
                    <Route path="/settings" component={pages.Settings} />
                    <Route path="/user" component={pages.User} />

                    {/* modules */}
                    <Route path="/inbox" component={pages.Inbox} />
                    <Route path="/webshop" component={pages.Webshop} />
                    <Route path="/webshop-plus" component={pages.WebshopPlus} />
                    <Route path="/website" component={pages.Website} />

                    <Route path="/logout" />

                    <UnauthorizedRoute path="/404" component={NotFound} />
                    <UnauthorizedRoute component={NotFound} />
                </Switch>

                <TrialExpiredDialog />
                <GettingStartedDialogWrapper />
                <VismaConnectCongratsDialog />
                <VismaConnectJoinDialog />

                <LegacyDialogRoutes />
            </ErrorBoundary>
        </Economic>
    );
}

/**
 * Wrap GettingStartedDialog and trigger home tour after it is done.
 * This component must be under EContext context provider.
 */
function GettingStartedDialogWrapper() {
    const [, setShowTour] = useIntroductionTour();

    function gettingStartedFinished() {
        setShowTour(true);
    }

    return <GettingStartedDialog onDone={gettingStartedFinished} />;
}
